<script setup lang="ts">
import {
  useGeolocationStore,
  formatDistance,
  type NearbyExplosion,
  type NearbyTrajectory,
} from '@/modules/geolocation';
import { formatRelativeTime } from '@/utils/time';
import { EXPLOSION_CLASS_COLORS, EXPLOSION_CLASS_LABELS } from '@/types/geolocation';

/** Лента ближайших событий: взрывы (точки) + пролёты дронов (траектории). Клик фокусирует карту. */

defineProps<{ locating: boolean }>();
const emit = defineEmits<{
  focus: [e: NearbyExplosion];
  focusTrajectory: [t: NearbyTrajectory];
}>();

const store = useGeolocationStore();
</script>

<template>
  <section class="feed">
    <div
      v-if="store.nearbyCount === 0 && store.nearbyTrajectories.length === 0 && !locating"
      class="empty"
    >
      За последнее время рядом ничего не зафиксировано.
    </div>

    <!-- Пролёты дронов: траектория со стрелкой курса; клик центрирует карту на пути -->
    <button
      v-for="t in store.nearbyTrajectories"
      :key="`traj-${t.id}`"
      class="card"
      @click="emit('focusTrajectory', t)"
    >
      <span
        class="arrow"
        :style="{ color: EXPLOSION_CLASS_COLORS[t.className], transform: `rotate(${t.headingDeg - 90}deg)` }"
      >➤</span>
      <div class="card-body">
        <div class="card-title">
          {{ EXPLOSION_CLASS_LABELS[t.className] || t.className }} · пролёт
          <span class="card-dist">{{ formatDistance(t.distanceM) }}</span>
        </div>
        <div class="card-meta">
          курс {{ t.headingDeg.toFixed(0) }}° · {{ (t.speedMps * 3.6).toFixed(0) }} км/ч
        </div>
        <div class="card-time">
          {{ formatRelativeTime(t.createdAt) }}
        </div>
      </div>
    </button>

    <!-- Взрывы: точечные события -->
    <button
      v-for="e in store.nearbyExplosions"
      :key="e.id"
      class="card"
      @click="emit('focus', e)"
    >
      <span
        class="dot"
        :style="{ background: EXPLOSION_CLASS_COLORS[e.className] }"
      />
      <div class="card-body">
        <div class="card-title">
          {{ EXPLOSION_CLASS_LABELS[e.className] || e.className }}
          <span class="card-dist">{{ formatDistance(e.distanceM) }}</span>
        </div>
        <div class="card-time">
          {{ formatRelativeTime(e.createdAt) }}
        </div>
      </div>
    </button>
  </section>
</template>

<style scoped>
.feed {
  flex: 1 1 auto;
  /* min-height:0 lets this flex child shrink below its content so the list scrolls instead of
     overflowing the feed-section (tabs stay pinned above). */
  min-height: 0;
  overflow-y: auto;
  padding: 10px 12px 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
  z-index: 1;
}
.empty {
  text-align: center;
  color: #7a8499;
  font-size: 14px;
  padding: 40px 20px;
}
.card {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  text-align: left;
  background: #18202f;
  border: 1px solid #232d40;
  border-radius: 12px;
  padding: 12px 14px;
  cursor: pointer;
  color: inherit;
}
.card:active {
  background: #1f2940;
}
.dot {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  flex-shrink: 0;
  box-shadow: 0 0 8px currentColor;
}
.arrow {
  flex-shrink: 0;
  width: 16px;
  text-align: center;
  font-size: 18px;
  line-height: 1;
  filter: drop-shadow(0 0 4px currentColor);
}
.card-meta {
  font-size: 12px;
  color: #aeb8cc;
  font-variant-numeric: tabular-nums;
  margin-top: 2px;
}
.card-body {
  flex: 1;
  min-width: 0;
}
.card-title {
  font-size: 15px;
  font-weight: 600;
  display: flex;
  justify-content: space-between;
  gap: 10px;
}
.card-dist {
  color: #2aabee;
  font-variant-numeric: tabular-nums;
}
.card-time {
  font-size: 12px;
  color: #8b94a7;
  margin-top: 2px;
}
</style>
